import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import SearchBar, { matchesSearch } from "../components/SearchBar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  EnvelopeSimple, CheckCircle, ArrowCounterClockwise, Trash,
  Buildings, User, Clock, PaperPlaneTilt, ChatCircleDots,
} from "@phosphor-icons/react";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
];

export default function RequestsInbox() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [projectMeta, setProjectMeta] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === "all" ? "/requests" : `/requests?status=${tab}`;
      const [reqRes, projRes] = await Promise.all([
        http.get(url),
        http.get("/projects").catch(() => ({ data: [] })),  // members can't fetch; fall back
      ]);
      setItems(reqRes.data);
      setProjectMeta(projRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try { await http.patch(`/requests/${id}/resolve`); toast.success("Marked resolved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reopen = async (id) => {
    try { await http.patch(`/requests/${id}/reopen`); toast.success("Reopened"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await http.delete(`/requests/${pendingDelete.id}`);
      toast.success("Deleted");
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  const clientByProject = useMemo(() => {
    const m = {};
    (projectMeta || []).forEach((p) => { m[p.name] = p.client_name || ""; });
    return m;
  }, [projectMeta]);

  const filteredItems = useMemo(() => {
    return items.filter((r) =>
      matchesSearch(search, r.project, r.client_name, clientByProject[r.project])
    );
  }, [items, search, clientByProject]);

  const grouped = useMemo(() => {
    const map = {};
    filteredItems.forEach((r) => {
      const k = `${r.project}::${r.task_id}::${r.task_title || "Deleted phase"}`;
      if (!map[k]) map[k] = { project: r.project, task_title: r.task_title || "Deleted phase", items: [] };
      map[k].items.push(r);
    });
    return Object.values(map);
  }, [filteredItems]);

  return (
    <Layout
      title="Client Requests"
      subtitle={canDelete
        ? "Details your clients have asked for on specific work phases. Resolve after you've replied."
        : "Client questions on the phases assigned to you. Reply to keep the client informed."}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6" data-testid="requests-toolbar">
        <div className="flex items-center gap-0 border border-zinc-300 bg-white w-fit" data-testid="requests-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 sm:px-5 py-2.5 label-overline border-r border-zinc-300 last:border-r-0 ${
                tab === t.key ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <SearchBar
          value={search}
          onChange={setSearch}
          testId="requests-search-input"
          placeholder="Search by Job No. or Client Name…"
          className="sm:ml-auto"
        />
      </div>

      {loading && <div className="label-overline text-zinc-500">Loading…</div>}

      {!loading && filteredItems.length === 0 && items.length > 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center bg-white" data-testid="requests-search-empty">
          <div className="label-overline text-zinc-500">No requests match &quot;{search}&quot;</div>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-16 text-center bg-white">
          <EnvelopeSimple size={36} weight="bold" className="mx-auto text-zinc-300" />
          <div className="label-overline text-zinc-500 mt-3">No {tab === "all" ? "" : tab} requests</div>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
            When clients submit questions from the public client portal, they&apos;ll show up here.
          </p>
        </div>
      )}

      {!loading && grouped.map((group) => (
        <div key={`${group.project}-${group.task_title}`} className="mb-6 border border-zinc-300 bg-white">
          <div className="px-5 py-3 border-b border-zinc-300 bg-zinc-50 flex items-center gap-3">
            <Buildings size={14} weight="bold" className="text-blue-600" />
            <span className="label-overline text-zinc-900">{group.project}</span>
            <span className="text-zinc-400">/</span>
            <span className="font-display font-bold text-zinc-900 truncate">{group.task_title}</span>
            <span className="ml-auto label-overline bg-zinc-900 text-white px-2 py-0.5">{group.items.length}</span>
          </div>
          <div>
            {group.items.map((r) => (
              <RequestRow key={r.id} r={r} canDelete={canDelete}
                          onResolve={resolve} onReopen={reopen}
                          onDelete={(row) => setPendingDelete(row)}
                          onReplied={load} />
            ))}
          </div>
        </div>
      ))}

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Destructive Action</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Delete this request?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will permanently delete the request from <strong>{pendingDelete?.client_name || "the client"}</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-request"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-request" disabled={deleting} onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs">
              {deleting ? "Deleting…" : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function RequestRow({ r, canDelete, onResolve, onReopen, onDelete, onReplied }) {
  const isPending = r.status === "pending";
  const hasReply = !!r.reply;
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const submitReply = async () => {
    const txt = replyText.trim();
    if (!txt) { toast.error("Reply cannot be empty"); return; }
    setSending(true);
    try {
      await http.patch(`/requests/${r.id}/reply`, { text: txt });
      toast.success("Reply sent");
      setReplyOpen(false); setReplyText("");
      if (onReplied) onReplied();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid={`request-row-${r.id}`}
      className={`p-5 border-b border-zinc-200 last:border-b-0 ${
        isPending ? "bg-orange-50/40" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`shrink-0 w-9 h-9 border flex items-center justify-center ${
          isPending ? "border-orange-500 text-orange-600" : "border-emerald-500 text-emerald-700"
        }`}>
          <User size={16} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-zinc-900">{r.client_name}</span>
            {r.client_contact && (
              <span className="text-xs text-zinc-500 font-mono">· {r.client_contact}</span>
            )}
            <span className={`label-overline ml-2 px-2 py-0.5 ${
              isPending ? "bg-orange-600 text-white" : "bg-emerald-100 text-emerald-800"
            }`}>{r.status}</span>
          </div>
          <div className="mt-2 text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
            {r.message}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-zinc-500 font-mono">
            <span className="flex items-center gap-1">
              <Clock size={11} weight="bold" /> {new Date(r.created_at).toLocaleString()}
            </span>
            {r.resolved_at && (
              <span className="flex items-center gap-1 text-emerald-700">
                <CheckCircle size={11} weight="bold" /> Resolved {new Date(r.resolved_at).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Existing reply displayed */}
          {hasReply && (
            <div className="mt-3 border-l-4 border-blue-600 bg-blue-50 px-3 py-2" data-testid={`reply-shown-${r.id}`}>
              <div className="label-overline text-blue-700 flex items-center gap-1.5">
                <ChatCircleDots size={11} weight="bold" /> Reply
                {r.replied_by && <span className="text-blue-800 normal-case tracking-normal">· {r.replied_by}</span>}
              </div>
              <div className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap">{r.reply}</div>
              {r.replied_at && (
                <div className="text-[10px] text-zinc-500 font-mono mt-1.5">
                  Sent {new Date(r.replied_at).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Reply composer */}
          {replyOpen && !hasReply && (
            <div className="mt-3 border border-blue-600 p-3 bg-white" data-testid={`reply-form-${r.id}`}>
              <textarea
                autoFocus
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                data-testid={`reply-input-${r.id}`}
                rows={3}
                maxLength={4000}
                placeholder="Write a clear, helpful reply for the client…"
                className="w-full rounded-none border border-zinc-300 px-2 py-2 text-sm focus:outline-none focus:border-blue-600 resize-y"
              />
              <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-zinc-200">
                <button
                  onClick={() => { setReplyOpen(false); setReplyText(""); }}
                  className="label-overline text-zinc-500 hover:text-zinc-900 px-3 py-1.5"
                >Cancel</button>
                <button
                  onClick={submitReply}
                  disabled={sending}
                  data-testid={`reply-submit-${r.id}`}
                  className="flex items-center gap-1.5 label-overline bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 disabled:opacity-60"
                >
                  <PaperPlaneTilt size={12} weight="bold" />
                  {sending ? "Sending…" : "Send Reply"}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {!hasReply && (
            <button
              onClick={() => setReplyOpen((v) => !v)}
              data-testid={`open-reply-${r.id}`}
              className="label-overline flex items-center gap-1.5 border border-blue-600 bg-blue-600 text-white px-3 py-1.5 hover:bg-blue-700"
            >
              <ChatCircleDots size={12} weight="bold" /> Reply
            </button>
          )}
          {isPending && (
            <button
              onClick={() => onResolve(r.id)}
              data-testid={`resolve-request-${r.id}`}
              className="label-overline flex items-center gap-1.5 border border-emerald-600 bg-white text-emerald-700 px-3 py-1.5 hover:bg-emerald-50"
            >
              <CheckCircle size={12} weight="bold" /> Resolve
            </button>
          )}
          {!isPending && (
            <button
              onClick={() => onReopen(r.id)}
              data-testid={`reopen-request-${r.id}`}
              className="label-overline flex items-center gap-1.5 border border-zinc-400 bg-white text-zinc-700 px-3 py-1.5 hover:bg-zinc-100"
            >
              <ArrowCounterClockwise size={12} weight="bold" /> Reopen
            </button>
          )}
          <button
            onClick={() => onDelete(r)}
            data-testid={`delete-request-${r.id}`}
            className={`label-overline flex items-center gap-1.5 text-zinc-400 hover:text-red-600 px-3 py-1.5 ${canDelete ? "" : "hidden"}`}
          >
            <Trash size={12} weight="bold" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
